import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import OutlineCard from "../../../../../components/OutlineCard";
import Text from "../../../../../components/Text";
import { PROVIDER_PATHS } from "../../../../../constants/paths";
import { useQueryParams } from "../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useGetClaimById } from "../../../../../queries/billings/claims/use-get-claim-by-id";
import { useCreateEra } from "../../../../../queries/billings/era/use-create-era";
import { useGetPatient } from "../../../../../queries/use-get-patient-uuid";
import {
  EraClaimRequestClaimStatusEnum,
  EraRequest,
  PatientProcedureCode,
} from "../../../../../services/api";
import { formatDate } from "../../../../../utils/date-time-utility";
import {
  getErrorSnackbar,
  getIdFromObj,
  getSuccessSnackbar,
  getValueFromObj,
} from "../../../../../utils/utils";
import ClaimsHeader from "./updateClaims/ClaimsHeader";
import NumberInput from "../../../../../components/NumberInput";

type ProcedureRow = {
  item: string;
  details: string;
  quantity: number;
  charge: string;
  payment: string;
  adjustment: string;
  adjustmentType: string;
  writeOff: string;
  writeOffType: string;
  balance: string;
  patientResponsibility: string;
  patientResponsibilityCode: string;
  patientResponsibilityType: string | null;
  copay: string;
  coInsurance: string;
  deductible: string;
  comments: string;
  denied: boolean;
  patientProcedureCode?: PatientProcedureCode;
};

type EraFormValues = {
  paymentAmount: string;
  paymentMethod: string | null;
  paymentDate: string | null;
  paymentFrom: string | null;
  provider: string | null;
  referenceNo: string;
  otherReferenceNo: string;
  procedures: ProcedureRow[];
  paymentDescription: string;
};

// Red asterisk marking a required field.
const RequiredMark = () => (
  <Box component="span" sx={{ color: "error.main" }}>
    *
  </Box>
);

const UpdateEraEobDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // When launched from Patient Charting → Financial, return to that origin
  // table on completion instead of the Billing Claims tab.
  const fromPatientChart = (location.state as any)?.from;
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const claimId = useQueryParams("claimId");
  const patientId = useQueryParams("patientId");
  const { data: patientData } = useGetPatient(patientId ?? undefined);
  const { mutateAsync: createEra } = useCreateEra();
  const { data: claimById, isLoading } = useGetClaimById({
    claimUuid: claimId ?? "",
  });
  const hasClaimData = !!(claimById as any)?.data;
  const isInitialClaimLoading = isLoading && !hasClaimData;

  const claimData = (claimById?.data ?? {}) as Record<string, any>;

  // todo: remove this after testingo
  useEffect(() => {}, [claimData]);

  const toFixedAmount = (value: unknown): any => {
    const num = Number(value ?? 0);

    if (!Number.isFinite(num)) return "";
    if (num === 0) return "";

    return num.toFixed(2);
  };

  const toNumber = (value: unknown) => {
    const num = Number(value ?? 0);
    return Number.isFinite(num) ? num : 0;
  };

  const mappedInitialValues = useMemo<EraFormValues>(() => {
    const procedures = (claimData?.patientProcedureCodes ??
      []) as PatientProcedureCode[];

    const mappedProcedures: ProcedureRow[] =
      procedures.length > 0
        ? procedures.map((proc) => {
            const charge = toNumber(proc?.charges ?? proc?.total);
            const quantity = toNumber(proc?.quantity) || 1;
            const payment = toNumber(proc?.insurancePaid);
            const adjustment = toNumber(proc?.adjustment);
            const writeOff = toNumber(proc?.writeOff);
            const patientResponsibility = toNumber(proc?.patientResponsibility);
            const balance =
              charge * quantity -
              payment -
              adjustment -
              writeOff -
              patientResponsibility;

            return {
              item: getValueFromObj(proc?.procedureCode) ?? "-",
              details: getValueFromObj(proc?.procedureCode) ?? "-",
              quantity,
              charge: toFixedAmount(charge),
              payment: toFixedAmount(payment),
              adjustment: toFixedAmount(adjustment),
              adjustmentType: "CONTRACT",
              writeOff: toFixedAmount(writeOff),
              writeOffType: "CONTRACT",
              balance: toFixedAmount(balance),
              patientResponsibility: toFixedAmount(patientResponsibility),
              patientResponsibilityCode: "",
              patientResponsibilityType: null,
              copay: toFixedAmount(proc?.copay),
              coInsurance: toFixedAmount(proc?.coInsurance),
              deductible: toFixedAmount(proc?.deductible),
              comments: "",
              denied: false,
              patientProcedureCode: proc,
            };
          })
        : [
            {
              item: "-",
              details: "-",
              quantity: 1,
              charge: toFixedAmount(claimData?.total),
              payment: "",
              adjustment: "",
              adjustmentType: "CONTRACT",
              writeOff: "",
              writeOffType: "CONTRACT",
              balance: toFixedAmount(claimData?.total),
              patientResponsibility: "",
              patientResponsibilityCode: "",
              patientResponsibilityType: null,
              copay: "",
              coInsurance: "",
              deductible: "",
              comments: "",
              denied: false,
            },
          ];

    return {
      paymentAmount: toFixedAmount(claimData?.total),
      paymentMethod: "Cheque",
      paymentDate: dayjs().toISOString(),
      paymentFrom: "Insurance",
      provider: getIdFromObj(claimData?.provider) ?? null,
      referenceNo:
        claimData?.uuid?.split("-")?.[0]?.toUpperCase() ??
        claimId?.split("-")?.[0]?.toUpperCase() ??
        "",
      otherReferenceNo: "",
      procedures: mappedProcedures,
      paymentDescription: "",
    };
  }, [claimData, claimId]);

  const requiresReferenceNo = (paymentMethod: string | null | undefined) =>
    paymentMethod === "Cheque" || paymentMethod === "Electronic";

  const validationSchema = useMemo(
    () =>
      Yup.object({
        paymentAmount: Yup.string()
          .required()
          .label("Payment Amount")
          .test(
            "payment-not-exceed-claim-due",
            "Payment Amount cannot exceed Claim Due",
            (val) => toNumber(val) <= toNumber(claimData?.total)
          ),
        paymentMethod: Yup.string().required().label("Payment Method"),
        paymentDate: Yup.string().required().label("Payment Date"),
        referenceNo: Yup.string().when("paymentMethod", {
          is: (val: string) => requiresReferenceNo(val),
          then: (schema) =>
            schema.required(
              "Reference No is required for Cheque/Electronic payments"
            ),
          otherwise: (schema) => schema,
        }),
        procedures: Yup.array().of(
          Yup.object({
            charge: Yup.string(),
            quantity: Yup.number(),
            denied: Yup.boolean(),
            // Payment, Adjustment, Write-off, Copay and Co-Insurance are
            // required (> 0) before a CPT can be posted; Payment also cannot
            // exceed the Charge. Denied lines are exempt — they carry no
            // payment and only need a comment.
            payment: Yup.string()
              .test("payment-required", "Payment is required", function (val) {
                const parent = this.parent as ProcedureRow;
                if (parent?.denied) return true;
                return toNumber(val) > 0;
              })
              .test(
                "payment-not-exceed-charge",
                "Payment cannot exceed Charge",
                function (val) {
                  const parent = this.parent as ProcedureRow;
                  if (parent?.denied) return true;
                  const chargeTotal =
                    parseFloat(parent?.charge || "0") * (parent?.quantity || 1);
                  return toNumber(val) <= chargeTotal;
                }
              ),
            adjustment: Yup.string()
              .test(
                "adjustment-required",
                "Adjustment is required",
                function (val) {
                  const parent = this.parent as ProcedureRow;
                  if (parent?.denied) return true;
                  return toNumber(val) > 0;
                }
              )
              .test(
                "adjustment-not-exceed-charge",
                "Adjustment + Write-off cannot exceed Charge",
                function (val) {
                  const parent = this.parent as ProcedureRow;
                  const chargeTotal =
                    parseFloat(parent?.charge || "0") * (parent?.quantity || 1);
                  const adj = parseFloat(val || "0");
                  const wo = parseFloat(parent?.writeOff || "0");
                  if (adj + wo > chargeTotal) {
                    return this.createError({
                      message: "Adj + Write-off cannot exceed Charge",
                    });
                  }
                  return true;
                }
              ),
            writeOff: Yup.string().test(
              "writeoff-not-exceed-charge",
              "Adjustment + Write-off cannot exceed Charge",
              function (val) {
                const parent = this.parent as ProcedureRow;
                const chargeTotal =
                  parseFloat(parent?.charge || "0") * (parent?.quantity || 1);
                const adj = parseFloat(parent?.adjustment || "0");
                const wo = parseFloat(val || "0");
                if (adj + wo > chargeTotal) {
                  return this.createError({
                    message: "Adj + Write-off cannot exceed Charge",
                  });
                }
                return true;
              }
            ),
            patientResponsibility: Yup.string().test(
              "copay-required",
              "Copay is required",
              function (val) {
                const parent = this.parent as ProcedureRow;
                if (parent?.denied) return true;
                return toNumber(val) > 0;
              }
            ),
            patientResponsibilityCode: Yup.string().test(
              "coinsurance-required",
              "Co-Insurance is required",
              function (val) {
                const parent = this.parent as ProcedureRow;
                if (parent?.denied) return true;
                return toNumber(val) > 0;
              }
            ),
          })
        ),
      }),
    [claimData?.total]
  );

  const buildEraPayload = (
    values: EraFormValues,
    isDraft: boolean
  ): EraRequest | null => {
    const billingProviderUuid =
      getIdFromObj(claimData?.billingProvider) ??
      getIdFromObj(claimData?.provider);
    const payerId = claimData?.patientInsurance?.insurancePayer?.payerId;
    const paymentDate = values.paymentDate
      ? dayjs(values.paymentDate).toISOString()
      : dayjs().toISOString();

    if (!billingProviderUuid || !payerId || !claimId) {
      showSnackbar({
        type: "error",
        message:
          "Billing provider, payer, or claim data is missing. Please verify claim details.",
      });
      return null;
    }

    const claimsChargeAmount = toNumber(
      claimData?.total || values.paymentAmount
    );
    const claimsInsurancePaid = toNumber(values.paymentAmount);
    const claimsInsuranceAdjustment = toNumber(
      values.procedures.reduce(
        (acc, proc) =>
          acc + toNumber(proc.adjustment) + toNumber(proc.writeOff),
        0
      )
    );
    const claimsPatientResponsibility = toNumber(
      values.procedures.reduce(
        (acc, proc) =>
          acc +
          toNumber(proc.patientResponsibility) +
          toNumber(proc.patientResponsibilityCode) +
          toNumber(proc.deductible),
        0
      )
    );
    const claimsAllowedAmount = Math.max(
      claimsChargeAmount - claimsInsuranceAdjustment,
      0
    );

    const patientProcedureCodes = values.procedures.reduce<
      PatientProcedureCode[]
    >((acc, proc) => {
      if (!proc.patientProcedureCode) return acc;
      acc.push({
        ...proc.patientProcedureCode,
        charges: toNumber(proc.charge),
        insurancePaid: toNumber(proc.payment),
        writeOff: toNumber(proc.writeOff),
        patientResponsibility:
          toNumber(proc.patientResponsibility) +
          toNumber(proc.patientResponsibilityCode) +
          toNumber(proc.deductible),
        copay: toNumber(proc.copay),
        coInsurance: toNumber(proc.coInsurance),
        deductible: toNumber(proc.deductible),
        adjustment: toNumber(proc.adjustment),
      });
      return acc;
    }, []);

    const claimStatus: EraClaimRequestClaimStatusEnum = isDraft
      ? EraClaimRequestClaimStatusEnum.Draft
      : EraClaimRequestClaimStatusEnum.Submitted;

    return {
      eraDocNumber:
        values.referenceNo ||
        claimData?.uuid?.split("-")?.[0]?.toUpperCase() ||
        claimId,
      eraDate: paymentDate,
      checkEftNumber:
        values.otherReferenceNo || values.referenceNo || undefined,
      paymentMethod: values.paymentMethod ?? undefined,
      amount: claimsInsurancePaid,
      billingProviderUuid,
      payerId,
      claims: [
        {
          claimId,
          payersClaimId: values.referenceNo || claimId,
          chargeAmount: claimsChargeAmount,
          insurancePaid: claimsInsurancePaid,
          insuranceAdjustment: claimsInsuranceAdjustment,
          patientResponsibility: claimsPatientResponsibility,
          allowedAmount: claimsAllowedAmount,
          adjudicationDate: paymentDate,
          paymentDate: paymentDate,
          statementDate: paymentDate,
          claimStatus,
          patientProcedureCodes:
            patientProcedureCodes.length > 0
              ? patientProcedureCodes
              : undefined,
        },
      ],
    };
  };

  const submitEra = async (values: EraFormValues, isDraft: boolean) => {
    const eraPayload = buildEraPayload(values, isDraft);
    if (!eraPayload) return;

    try {
      const res = await createEra({ eraRequest: eraPayload });
      const apiMessage = getSuccessSnackbar(res).message;
      showSnackbar({
        type: "success",
        message:
          apiMessage ||
          (isDraft
            ? "Draft saved successfully"
            : "Payment added to claim successfully"),
      });
      queryClient.invalidateQueries({ queryKey: ["get-all-eras"] });
      // localStorage.setItem("billingSelectedTab", "era_eob");
      navigate(fromPatientChart ?? PROVIDER_PATHS.CLAIMS);
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
    }
  };

  const formik = useFormik<EraFormValues>({
    initialValues: mappedInitialValues,
    validationSchema,
    onSubmit: (values) => submitEra(values, false),
    enableReinitialize: true,
  });

  const handleSaveDraft = async () => {
    await submitEra(formik.values, true);
  };

  const paymentMethodOptions = [
    { value: "Cheque", label: "Cheque" },
    { value: "Cash", label: "Cash" },
    { value: "Credit Card", label: "Credit Card" },
    { value: "Electronic", label: "Electronic" },
  ];

  const paymentFromOptions = [
    { value: "Insurance", label: "Insurance" },
    { value: "Patient", label: "Patient" },
  ];

  const providerOptions = [
    {
      value: getIdFromObj(claimData?.provider) ?? "",
      label: getValueFromObj(claimData?.provider) ?? "Provider",
    },
  ].filter((provider) => !!provider.value);

  const adjustmentTypeOptions = [
    { value: "CONTRACT", label: "Contract" },
    { value: "NON_COVERED", label: "Non Covered" },
  ];

  const calculateTotals = () => {
    const totals = formik.values.procedures.reduce(
      (acc, proc) => {
        return {
          charge:
            acc.charge + parseFloat(proc.charge || "0") * (proc.quantity || 1),
          payment: acc.payment + parseFloat(proc.payment || "0"),
          adjustment: acc.adjustment + parseFloat(proc.adjustment || "0"),
          writeOff: acc.writeOff + parseFloat(proc.writeOff || "0"),
          balance: acc.balance + parseFloat(proc.balance || "0"),
          patientResponsibility:
            acc.patientResponsibility +
            parseFloat(proc.patientResponsibility || "0") +
            parseFloat(proc.patientResponsibilityCode || "0") +
            parseFloat(proc.deductible || "0"),
        };
      },
      {
        charge: 0,
        payment: 0,
        adjustment: 0,
        writeOff: 0,
        balance: 0,
        patientResponsibility: 0,
      }
    );

    return totals;
  };

  const totals = calculateTotals();

  // Surface a procedure field's error only once it is dirty or the user has
  // attempted to submit, instead of prematurely on load.
  const procedureError = (index: number, field: string) => {
    const touched = (
      formik.touched.procedures?.[index] as Record<string, boolean> | undefined
    )?.[field];
    if (!touched && formik.submitCount === 0) return undefined;
    return (
      formik.errors.procedures?.[index] as Record<string, string> | undefined
    )?.[field];
  };

  const handleProcedureChange = (index: number, field: string, value: any) => {
    formik.setFieldTouched(`procedures.${index}.${field}`, true, false);
    const procedures = [...formik.values.procedures];
    procedures[index] = { ...procedures[index], [field]: value };

    // Recalculate balance
    const charge = parseFloat(procedures[index].charge || "0");
    const quantity = procedures[index].quantity || 1;
    const payment = parseFloat(procedures[index].payment || "0");
    const adjustment = parseFloat(procedures[index].adjustment || "0");
    const writeOff = parseFloat(procedures[index].writeOff || "0");
    const patientResp = parseFloat(
      procedures[index].patientResponsibility || "0"
    );

    procedures[index].balance = (
      charge * quantity -
      payment -
      adjustment -
      writeOff -
      patientResp
    ).toFixed(2);

    formik.setFieldValue("procedures", procedures);
  };

  return (
    <OutlineCard sx={{ p: 1.8, height: "calc(100vh - 4.4rem)" }}>
      <Flex height={"100%"} gap={1}>
        <Flex flexDirection={"column"} width={"100%"} gap={1}>
          <LoadingOverlay isLoading={isInitialClaimLoading} />
          <ClaimsHeader
            patientsData={patientData}
            title="Add Payment to Claim"
          />
          <Divider sx={{ my: 1 }} />

          <Box
            flex={1}
            minHeight={0}
            boxSizing="border-box"
            overflow="auto"
            p={1}
          >
            <Grid container spacing={1}>
              {/* Claim Information Section */}
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  <Grid item xs={3}>
                    <Input
                      label="Visit Date"
                      textFieldProps={{
                        value: formatDate(claimData?.dateOfService) ?? "-",
                        disabled: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Input
                      label="Claim #"
                      textFieldProps={{
                        value:
                          claimData?.uuid?.split("-")?.[0]?.toUpperCase() ??
                          claimId?.split("-")?.[0]?.toUpperCase() ??
                          "-",
                        disabled: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Input
                      label="Date"
                      textFieldProps={{
                        value: formik.values.paymentDate
                          ? formatDate(formik.values.paymentDate)
                          : "-",
                        disabled: true,
                      }}
                    />
                  </Grid>
                  {/* <Grid item xs={3}>
                    <Input
                      label="Amount"
                      textFieldProps={{
                        value: toFixedAmount(formik.values.paymentAmount),
                        disabled: true,
                      }}
                    />
                  </Grid> */}
                  {/* <Grid item xs={3}>
                    <Input
                      label="Due"
                      textFieldProps={{
                        value: toFixedAmount(
                          toNumber(claimData?.total) -
                            toNumber(formik.values.paymentAmount)
                        ),
                        disabled: true,
                      }}
                    />
                  </Grid> */}
                  <Grid item xs={3}>
                    <Input
                      label="Claim Amount"
                      textFieldProps={{
                        value: toFixedAmount(claimData?.total),
                        disabled: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Input
                      label="Claim Due"
                      textFieldProps={{
                        value: toFixedAmount(claimData?.total),
                        disabled: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Input
                      label="Payer"
                      textFieldProps={{
                        value:
                          claimData?.patientInsurance?.insurancePayer
                            ?.payerName ?? "-",
                        disabled: true,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Service Details Section */}
              <Grid item xs={12}>
                <Text fontWeight="bold" mb={1} mt={1.5} variant="h6">
                  Service Details
                </Text>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Input
                      label="Payment Amount"
                      textFieldProps={{
                        name: "paymentAmount",
                        value: formik.values.paymentAmount,
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        placeholder: "",
                      }}
                      error={
                        formik.touched.paymentAmount &&
                        (formik.errors.paymentAmount as string)
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomSelect
                      required
                      name="paymentMethod"
                      handleBlur={formik.handleBlur}
                      label="Payment Method"
                      placeholder="Select Payment Method"
                      options={paymentMethodOptions}
                      value={formik.values.paymentMethod}
                      onChange={(v) => {
                        formik.setFieldValue("paymentMethod", v);
                        formik.setFieldTouched("paymentMethod", true, false);
                      }}
                      error={
                        formik.touched.paymentMethod &&
                        (formik.errors.paymentMethod as string)
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomDatePicker
                      required
                      name="paymentDate"
                      handleBlur={formik.handleBlur}
                      label="Payment Date"
                      value={formik.values.paymentDate}
                      onChange={(val) => {
                        formik.setFieldValue("paymentDate", val);
                        formik.setFieldTouched("paymentDate", true, false);
                      }}
                      error={
                        formik.touched.paymentDate &&
                        (formik.errors.paymentDate as string)
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomSelect
                      label="Payment From"
                      placeholder="Select Payment From"
                      options={paymentFromOptions}
                      value={formik.values.paymentFrom}
                      onChange={(v) => formik.setFieldValue("paymentFrom", v)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomSelect
                      label="Provider"
                      placeholder="Select Provider"
                      options={providerOptions}
                      value={formik.values.provider}
                      onChange={(v) => formik.setFieldValue("provider", v)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Input
                      required={requiresReferenceNo(
                        formik.values.paymentMethod
                      )}
                      label="Reference No #"
                      textFieldProps={{
                        name: "referenceNo",
                        value: formik.values.referenceNo,
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        placeholder: "Reference No #",
                      }}
                      error={
                        formik.touched.referenceNo &&
                        (formik.errors.referenceNo as string)
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Input
                      label="Other Reference No #"
                      textFieldProps={{
                        name: "otherReferenceNo",
                        value: formik.values.otherReferenceNo,
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        placeholder: "Enter Other Reference No #",
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Procedures Table */}
              <Grid item xs={12}>
                <Text fontWeight="bold" mb={1} mt={1.5} variant="h6">
                  Procedures
                </Text>
                <TableContainer component={Paper} variant="outlined">
                  <Table
                    size="small"
                    sx={{ tableLayout: "fixed", minWidth: 1280 }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 180 }}>Item</TableCell>
                        <TableCell sx={{ width: 220 }}>Details</TableCell>
                        <TableCell sx={{ width: 80 }}>Charge</TableCell>
                        <TableCell sx={{ width: 130 }}>
                          Payment <RequiredMark />
                        </TableCell>
                        <TableCell sx={{ width: 140 }}>
                          Adjustment <RequiredMark />
                        </TableCell>
                        <TableCell sx={{ width: 140 }}>Write-off</TableCell>
                        <TableCell sx={{ width: 90 }}>Balance</TableCell>
                        <TableCell sx={{ width: 320 }}>
                          Patient Responsibility
                        </TableCell>
                        <TableCell sx={{ width: 140 }}>Comments</TableCell>
                        <TableCell sx={{ width: 70 }}>Denied</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formik.values.procedures.map((proc, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ width: 220, verticalAlign: "top" }}>
                            {proc.item}
                          </TableCell>
                          <TableCell sx={{ width: 150, verticalAlign: "top" }}>
                            {proc.details}
                          </TableCell>
                          <TableCell sx={{ width: 80, verticalAlign: "top" }}>
                            <Flex flexDirection="column" gap={0.25}>
                              <Box>
                                {proc.charge} × {proc.quantity}
                              </Box>
                            </Flex>
                          </TableCell>
                          <TableCell sx={{ width: 130, verticalAlign: "top" }}>
                            <NumberInput
                              error={procedureError(index, "payment")}
                              textFieldProps={{
                                value: proc.payment,
                                onChange: (e) =>
                                  handleProcedureChange(
                                    index,
                                    "payment",
                                    e.target.value
                                  ),
                                sx: { width: "120px" },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 140, verticalAlign: "top" }}>
                            <Flex
                              flexDirection={"column"}
                              gap={1}
                              sx={{ width: 120 }}
                            >
                              <NumberInput
                                error={procedureError(index, "adjustment")}
                                textFieldProps={{
                                  value: proc.adjustment,
                                  onChange: (e) =>
                                    handleProcedureChange(
                                      index,
                                      "adjustment",
                                      e.target.value
                                    ),
                                }}
                              />
                              <CustomSelect
                                flexProps={{ width: "120px" }}
                                options={adjustmentTypeOptions}
                                value={proc.adjustmentType}
                                onChange={(v) =>
                                  handleProcedureChange(
                                    index,
                                    "adjustmentType",
                                    v
                                  )
                                }
                                placeholder="Select"
                                clearable={false}
                              />
                            </Flex>
                          </TableCell>
                          <TableCell sx={{ width: 140, verticalAlign: "top" }}>
                            <Flex
                              flexDirection="column"
                              gap={1}
                              sx={{ width: 120 }}
                            >
                              <NumberInput
                                error={procedureError(index, "writeOff")}
                                textFieldProps={{
                                  value: proc.writeOff,
                                  onChange: (e) =>
                                    handleProcedureChange(
                                      index,
                                      "writeOff",
                                      e.target.value
                                    ),
                                  sx: { width: "120px" },
                                }}
                              />
                              <CustomSelect
                                flexProps={{ width: "120px" }}
                                options={adjustmentTypeOptions}
                                value={proc.writeOffType}
                                onChange={(v) =>
                                  handleProcedureChange(
                                    index,
                                    "writeOffType",
                                    v
                                  )
                                }
                                placeholder="Select"
                                clearable={false}
                              />
                            </Flex>
                          </TableCell>
                          <TableCell sx={{ width: 90, verticalAlign: "top" }}>
                            {proc.balance}
                          </TableCell>
                          <TableCell sx={{ width: 320, verticalAlign: "top" }}>
                            <Flex gap={1} width="100%">
                              <Flex flexDirection="column" gap={0.5} flex={1}>
                                <NumberInput
                                  error={procedureError(
                                    index,
                                    "patientResponsibility"
                                  )}
                                  textFieldProps={{
                                    size: "small",
                                    value: proc.patientResponsibility,
                                    onChange: (e) =>
                                      handleProcedureChange(
                                        index,
                                        "patientResponsibility",
                                        e.target.value
                                      ),
                                  }}
                                />
                                <Button
                                  size="small"
                                  fullWidth
                                  variant={
                                    proc.patientResponsibilityType === "Copay"
                                      ? "contained"
                                      : "outlined"
                                  }
                                  onClick={() =>
                                    handleProcedureChange(
                                      index,
                                      "patientResponsibilityType",
                                      "Copay"
                                    )
                                  }
                                >
                                  Copay <RequiredMark />
                                </Button>
                              </Flex>
                              <Flex flexDirection="column" gap={0.5} flex={1}>
                                <NumberInput
                                  error={procedureError(
                                    index,
                                    "patientResponsibilityCode"
                                  )}
                                  textFieldProps={{
                                    size: "small",
                                    value: proc.patientResponsibilityCode,
                                    onChange: (e) =>
                                      handleProcedureChange(
                                        index,
                                        "patientResponsibilityCode",
                                        e.target.value
                                      ),
                                  }}
                                />
                                <Button
                                  size="small"
                                  fullWidth
                                  variant={
                                    proc.patientResponsibilityType ===
                                    "Co-Insurance"
                                      ? "contained"
                                      : "outlined"
                                  }
                                  onClick={() =>
                                    handleProcedureChange(
                                      index,
                                      "patientResponsibilityType",
                                      "Co-Insurance"
                                    )
                                  }
                                >
                                  Co-Insurance <RequiredMark />
                                </Button>
                              </Flex>
                              <Flex flexDirection="column" gap={0.5} flex={1}>
                                <NumberInput
                                  textFieldProps={{
                                    size: "small",
                                    value: proc.deductible,
                                    onChange: (e) =>
                                      handleProcedureChange(
                                        index,
                                        "deductible",
                                        e.target.value
                                      ),
                                  }}
                                />
                                <Button
                                  size="small"
                                  fullWidth
                                  variant={
                                    proc.patientResponsibilityType ===
                                    "Deductible"
                                      ? "contained"
                                      : "outlined"
                                  }
                                  onClick={() =>
                                    handleProcedureChange(
                                      index,
                                      "patientResponsibilityType",
                                      "Deductible"
                                    )
                                  }
                                >
                                  Deductible
                                </Button>
                              </Flex>
                            </Flex>
                          </TableCell>
                          <TableCell sx={{ width: 140, verticalAlign: "top" }}>
                            <CustomTextArea
                              minRows={2}
                              placeholder="Comments"
                              value={proc.comments}
                              onChange={(e) =>
                                handleProcedureChange(
                                  index,
                                  "comments",
                                  e.target.value
                                )
                              }
                              sx={{ width: 170 }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 70, verticalAlign: "top" }}>
                            <Checkbox
                              checked={proc.denied}
                              onChange={(e) =>
                                handleProcedureChange(
                                  index,
                                  "denied",
                                  e.target.checked
                                )
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell colSpan={2}>
                          <Text fontWeight="bold">Total</Text>
                        </TableCell>
                        <TableCell>
                          <Text fontWeight="bold">
                            {totals.charge.toFixed(2)}
                          </Text>
                        </TableCell>
                        <TableCell>
                          <Text fontWeight="bold">
                            {totals.payment.toFixed(2)}
                          </Text>
                        </TableCell>
                        <TableCell>
                          <Text fontWeight="bold">
                            {totals.adjustment.toFixed(2)}
                          </Text>
                        </TableCell>
                        <TableCell>
                          <Text fontWeight="bold">
                            {totals.writeOff.toFixed(2)}
                          </Text>
                        </TableCell>
                        <TableCell>
                          <Text fontWeight="bold">
                            {totals.balance.toFixed(2)}
                          </Text>
                        </TableCell>
                        <TableCell>
                          <Text fontWeight="bold">
                            {totals.patientResponsibility.toFixed(2)}
                          </Text>
                        </TableCell>
                        <TableCell colSpan={2}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {/* Payment Description */}
              <Grid item xs={12} mt={2}>
                <CustomTextArea
                  label="Payment Description"
                  minRows={4}
                  value={formik.values.paymentDescription}
                  onChange={formik.handleChange}
                  name="paymentDescription"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          {/* Action Buttons - sticky footer outside scroll area */}
          <Flex justifyContent="flex-end" gap={2} p={1} pt={2}>
            <Button
              variant="outlined"
              onClick={handleSaveDraft}
              disabled={isInitialClaimLoading || formik.isSubmitting}
            >
              Save As Draft
            </Button>
            <Button
              variant="contained"
              onClick={() => formik.submitForm()}
              disabled={isInitialClaimLoading || formik.isSubmitting}
            >
              Add Payment to Claim
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </OutlineCard>
  );
};

export default UpdateEraEobDetails;
